import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import {
     loginWithEmail,
     signInWithGoogle,
     logoutUser,
     removeCurrentAuthUser,
} from '../firebase/auth';
import { saveUserToLocal, getAuthFlag, clearUserFromLocal } from '../utils/authUser';
import InfoLists from '../component/InfoLists';
import SEO, { seoConfig } from '../component/SEO';
import { toastedError, toastedSuccess } from '../config/toast';
import { title } from '../config/config';

const Login = () => {
     const navigate = useNavigate();

     const [formData, setFormData] = useState({
          email: '',
          password: '',
     });

     const [loading, setLoading] = useState(false);
     const [googleLoading, setGoogleLoading] = useState(false);
     const [errorText, setErrorText] = useState('');

     useEffect(() => {
          if (getAuthFlag()) {
               navigate('/overview');
          }
     }, [navigate]);

     const handleChange = (event) => {
          const { name, value } = event.target;

          setFormData((prev) => ({
               ...prev,
               [name]: value,
          }));
     };

     const getFirebaseError = (error) => {
          switch (error.code) {
               case 'auth/invalid-email':
                    return 'Invalid email address.';
               case 'auth/user-not-found':
               case 'auth/invalid-credential':
               case 'auth/wrong-password':
                    return 'Email or password is incorrect.';
               case 'auth/too-many-requests':
                    return 'Too many attempts. Please try again later.';
               case 'auth/popup-closed-by-user':
                    return 'Google login popup was closed.';
               default:
                    return error.message || 'Login failed. Please try again.';
          }
     };

     const handleEmailLogin = async (event) => {
          event.preventDefault();
          setErrorText('');

          if (!formData.email.trim() || !formData.password.trim()) {
               setErrorText('Email and password are required.');
               toastedError('Email and password are required.')
               return;
          }

          try {
               setLoading(true);

               const user = await loginWithEmail(
                    formData.email.trim(),
                    formData.password
               );

               await saveUserToLocal(user);
               navigate('/overview');
               toastedSuccess("Login successful 🚀 Let's build something great");
          } catch (error) {
               console.error('Email login error:', error);
               setErrorText(getFirebaseError(error));
          } finally {
               setLoading(false);
          }
     };

     const handleGoogleLogin = async () => {
          setErrorText('');

          try {
               setGoogleLoading(true);

               const result = await signInWithGoogle();
               const user = result.user;
               const isNewUser = result?.user?.metadata?.creationTime === result?.user?.metadata?.lastSignInTime
                    || result?.additionalUserInfo?.isNewUser;

               if (isNewUser) {
                    clearUserFromLocal();

                    try {
                         await removeCurrentAuthUser();
                    } catch (deleteError) {
                         console.error('New Google user delete error:', deleteError);
                         await logoutUser();
                    }

                    setErrorText('This account is not registered yet. Please create an account first.');
                    toastedError('This account is not registered yet. Please create an account first.');
                    return;
               }

               await saveUserToLocal(user);
               navigate('/overview');
               toastedSuccess("Login successful 🚀 Let's build something great");

          } catch (error) {
               console.error('Google login error:', error);
               setErrorText(getFirebaseError(error));
               toastedError(error);
          } finally {
               setGoogleLoading(false);
          }
     };

     return (
          <>

          <SEO {...seoConfig.login} />
          <div className="login-hero-theme">
               <div className="floating-elements">
                    <div className="el sphere" />
                    <div className="el cube" />
                    <div className="el torus" />
                    <div className="el triangle" />
               </div>

               <main className="login-card-v2">
                    <div className="badge">With {title}</div>

                    <header className="login-intro">
                         <h1>
                              Forge your path <span>today.</span>
                         </h1>
                         <p>
                              A minimal workspace to capture, organize, and validate your concepts.
                              Start your journey in minutes.
                         </p>
                    </header>

                    {errorText && <div className="login-error text-danger">{errorText}</div>}

                    <form className="login-form-box" onSubmit={handleEmailLogin}>
                         <div className="input-group">
                              <label>Email</label>
                              <input
                                   type="email"
                                   name="email"
                                   value={formData.email}
                                   onChange={handleChange}
                                   placeholder="Enter your email"
                              />
                         </div>

                         <div className="input-group">
                              <label>Password</label>
                              <input
                                   type="password"
                                   name="password"
                                   value={formData.password}
                                   onChange={handleChange}
                                   placeholder="Enter your password"
                              />
                         </div>

                         

                         <div className="login-actions">
                              <button
                                   type="submit"
                                   className="primary-login-btn"
                                   disabled={loading || googleLoading}
                              >
                                   {loading ? 'Signing in...' : 'Login'}
                              </button>

                              <button
                                   type="button"
                                   className="google-btn-premium"
                                   onClick={handleGoogleLogin}
                                   disabled={loading || googleLoading}
                              >
                                   <img
                                        src="https://www.gstatic.com/firebasejs/ui/2.0.0/images/auth/google.svg"
                                        alt="Google"
                                   />
                                   <span>
                                        {googleLoading ? 'Checking...' : 'Continue with Google'}
                                   </span>
                              </button>
                         </div>
                    </form>

                    <div className="auth-bottom-link">
                         Don&apos;t have an account? <Link to="/register">Create one</Link>
                    </div>

                     <InfoLists/>
               </main>
          </div>
          </>

     );
};

export default Login;